import React, { useEffect, useMemo, useState } from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import MaynLayout from '../../../Core/MainLayout';
import Dashboard from '../../../Core/Dashboard';
import TitleBlock from '../../../Core/TitleBlock';
import PageLayout from '../../../Core/PageLayout';
import Progress from '../../../Core/Progress';
import {
  AccountGroupsSelector, employeesSelector,
  JournalDataSelector,
  isLoadingSelector, isShowSnackbar, permissionsSelector, rolesLoading, rolesSelector, snackbarText, snackbarType,
} from '../../../../store/settings/selectors';
import { userSelector } from '../../../../store/auth/selectors';
import RolesIcon from '../../../Icons/RolesIcon';
import RolesBlock from './RoleDetails/RolesBlock';
import {
  createRole,
  deleteRole,
  getAccountGroups,
  getRoles,
  loadEmployeesAll,
  loadPermissions,
  updateRole,
  loadLogbookJournal,
  loadIntegrations,
} from '../../../../store/settings/actions';
import AddEditItem from '../../../Core/Dialog/AddEditItem';
import usePermissions from '../../../Core/usePermissions';
import { companyModules } from '../../../../store/company/selectors';
import { IntegrationsDataSelector } from '../../../../store/settings/selectors';

const useStyles = makeStyles(() => ({
  error: {
    background: '#de4343',
    color: '#fff',
  },
  success: {
    background: '#3bc39e',
    color: '#fff',
  },
}));

const permissionsConfig = [
  {
    name: 'reports',
    module: 'reports',
  },
  {
    name: 'schedule_shift',
    module: 'schedule_shift',
  },
  {
    name: 'schedule_simple',
    module: 'schedule_simple',
  },
  {
    name: 'events',
    module: 'events',
  },
  {
    name: 'logbook',
    module: 'logbook',
  },
  {
    name: 'use_approval_flow',
    module: 'use_approval_flow',
  },
  {
    name: 'cost_earning',
    module: 'cost_earning',
  },
  {
    name: 'profitability',
    module: 'profitability',
  },
  {
    name: 'activity_log',
    module: 'activity_log',
  },
  {
    name: 'time_sheet',
    module: 'time_sheet',
  },
  {
    name: 'integrations',
    module: 'integrations',
  },
  {
    name: 'comments_photo',
    module: 'comments_photo',
  },
  {
    name: 'time_off',
    module: 'time_off',
  }, {
    name: 'places',
    module: 'places',
  }, {
    name: 'places_use_documents',
    module: 'places_use_documents',
  }, {
    name: 'invoices',
    module: 'invoices',
  }, {
    name: 'places_profitability',
    module: 'places_profitability',
  }, {
    name: 'iiko_module',
    module: 'iiko_profitability'
  }, {
    name: 'documents',
    module: 'documents'
  },
];
export default () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const permissions = usePermissions(permissionsConfig);

  const isLoading = useSelector(isLoadingSelector);
  const isSnackbar = useSelector(isShowSnackbar);
  const typeSnackbar = useSelector(snackbarType);
  const textSnackbar = t(useSelector(snackbarText));
  const user = useSelector(userSelector);
  const roles = useSelector(rolesSelector);
  const loading = useSelector(rolesLoading);
  const allPermissions = useSelector(permissionsSelector);
  const journal = useSelector(JournalDataSelector);
  const { users: employees } = useSelector(employeesSelector);
  const groups = useSelector(AccountGroupsSelector);
  const [activeRole, setActiveRole] = useState({});
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [defaultRoleAccess, setDefaultRoleAccess] = useState({});
  const modules = useSelector(companyModules);
  const integrations = useSelector(IntegrationsDataSelector);

  const initialRoleAccess = {
    // Access by Module
    moduleAccess: {
      overview: {
        options: {
          hide_hours: 'Hide hours (only work day amount)',
        },
      },
      ...(modules.logbook && {
        logbook: {
          options: {
            assigned_place: 'Only assigned place view',
            add_logs: 'Can create new entries',
            edit_logs: 'Can edit entry logs',
            ...(modules.comments_photo && { edit_comments: 'Can edit entry comments' }),
            ...(modules.custom_category && { can_edit_categories: 'Can edit entry additional categories' }),
            delete_logs: 'Can delete entry logs',
            ...(modules.cost_earning && { earnings: 'Can see earnings (APP)' }),
            ...(modules.profitability && { profit: 'Can see earnings and profit' }),
            ...(modules.cost_earning && { costs: 'Can see costs' }),
            requests: 'Get approval requests',
            requests_in_place: 'Get approval requests in assigned place',
            receive_notifications: 'Receive notifications',
          },
        },
      }),
      ...(modules.time_sheet && {
        time_sheet: {
          options: {
            ...(modules.cost_earning && { costs: 'Can see costs' }),
            place: 'Time Sheets only for assigned place',
          },
        },
      }),
      ...(modules.integrations && {
        integrations: {
          options: {
            
          },
        },
      }),
      ...(modules.reports && {
        reports: {
          options: {
            ...(modules.cost_earning && { costs: 'Can see costs' }),
            ...(modules.profitability && { earnings: 'Can see earnings' }),
            ...(modules.profitability && { profit: 'Can see profit' }),
            generate: 'Can generate reports',
            assigned_place: 'Reports only for assigned place',
          },
        },
      }),
      ...(modules.events && {
        events: {
          options: {
            receive_app: 'Receive notifications',
            ...(journal.geolocation_push_notification && { receive_geo_leave: 'Geo Leave' }),
          },
        },
      }),
      ...((modules.schedule_shift || modules.schedule_simple) && {
        schedule: {
          options: {
            create_and_edit: 'Can create & edit schedules',
            can_edit_history: 'Can edit schedule history',
            view: 'Can see Schedule',
            assigned_place: 'Only assigned place view',
            see_all_employees: 'Can see all employees',
            costs: 'Can see Costs',
          },
        },
      }),
      ...((modules.time_off) && {
        time_off: {
          options: {
            see_people_availability: 'See peoples availability (APP)',
            receive_notifications: 'Receive notifications',
            see_all_requests: 'Can see all requests (Requests)',
            see_my_employees: 'Can see My Employees',
            fill_request_behalf: 'Can fill request on behalf of (My Employees)',
            adjust_balance: 'Can adjust balance (My Employees)',
            adjust_time_used: 'Can adjust time used (My Employees)',
            see_private_requests: 'Can see private requests',
          },
        },
      }),
      ...(modules.places && {
        places: {
          options: {
            can_manage_documents: 'Add and edit access in documents',
            can_see_attendance: 'See attendance',
            ...(modules.use_expenses && {can_view_expenses: 'View expenses', can_manage_expenses: 'Add and edit expenses'}),
            can_see_profitability: 'See profitability',
            ...(integrations.use_iiko_profitability && modules.iiko_profitability && {see_iiko_profitability: 'See iiko profitability'})
          },
        },
      }),
      ...(modules.invoices && {
        invoices: {
          options: {
            create_edit_drafts: 'Can create and edit drafts',
            can_edit_invoice_settings: 'Can edit invoice settings',
          }
        }
      }),
      ...(modules.documents && {
        documents: {
          options: {
            can_upload_documents: 'Can upload documents'
          },
        },
      })
    },
  
    // Organization access
    organisation: {
      groups: {
        options: {
          ...(modules.create_groups && { create: 'Can create Groups' }),
        },
      },
      roles: {
        options: {
          create: 'Can create Roles',
        },
      },
      categories: {
        options: {
          create: 'Can create Categories',
        },
      },
      events: {
        options: {
          ...(modules.events && { create: 'Can create Events' }),
        },
      },
      data: {
        options: {
          delete: 'Can delete entry data',
        },
      },
      accounts: {
        options: {
          create: 'Can create New accounts',
          delete: 'Can delete Accounts list',
          see_and_edit: 'Can see & edit Accounts List',
        },
      },
      activity_log: {
        options: {
          view: 'Can see Activity Log',
        },
      },
      company: {
        options: {
          edit_settings: 'Can edit General Settings',
        },
      },
      logbook: {
        options: {
          edit_settings: 'Can edit Logbook settings',
        }
      },
      manager: {
        options: {
          ...(modules.use_manager_mobile && { mobile: 'Use Managers Mobile View' }),
          ...(modules.use_manager_mobile && { clock_in: 'Manager can clock in' }),
          ...(modules.use_manager_mobile && { start: 'Manager can start the time for others' }),
        },
      },
      app: {
        options: {
          manager: 'Managers view WEB'
        }
      },
      kiosk: {
        options: {
          ...(modules.kiosk && { create: 'Can create Kiosks & PIN' }),
        },
      },
      schedule: {
        options: {
          ...(modules.schedule_shift && { edit: 'Can edit Schedule settings' }),
          ...(modules.schedule_simple && { edit_settings: 'Can edit Tasker settings' }),
        },
      },
      time_sheet: {
        options: {
          ...(modules.time_sheet && { edit_settings: 'Can edit Time Sheet settings' }),
        },
      },
      integrations: {
        options: {
          ...(modules.integrations && { edit_settings: 'Can edit Integration settings' }),
        },
      },
      ...((modules.time_off) && {
        time_off: {
          options: {
            edit_settings: 'Can edit Time Off settings',
          },
        },
      }),
      ...((modules.documents) && {
        documents: {
          options: {
            can_edit_settings: 'Can edit Documents settings',
          },
        },
      }),
    },
  };

  const permissionsIds = allPermissions.reduce((acc, perm) => {
    return {
      ...acc,
      [perm.name]: {
        ...(acc[perm.name] || {}),
        [perm.action]: perm.id,
      },
    }
  }, {
  })

  const roleEmployeesEdit = async (data) => {
    dispatch(updateRole(id, activeRole.id, { users: data?.length ? data.toString() : '' }));
  };

  const removeRolesPermissions = (data = []) => {
    // eslint-disable-next-line no-shadow
    dispatch(updateRole(id, activeRole.id, { permissions: data.map((id) => ({ id, access: 0 })) }));
  };

  const rolesPermissionsEdit = (data) => {
    // eslint-disable-next-line no-shadow
    
    const oldRolePermissions = roles.find(({ id }) => id === activeRole.id)
      ?.accountRolesPermissions
      // eslint-disable-next-line no-shadow
      ?.map(({ permission_id: id, access }) => ({ id, access }));
    const newPermissions = data?.length ? data.map((item) => ({
      id: item, access: 1,
    })) : [];

    // console.log(_.unionWith(oldRolePermissions, newPermissions, _.isEqual));
    // eslint-disable-next-line no-nested-ternary,no-shadow
    const oldFiltered = oldRolePermissions.map(({ id, access }) => (newPermissions.some((i) => id === i.id)
      ? access
        ? ({ id, access })
        : ({ id, access: newPermissions.find((item) => item.id).access })
      : ({ id, access: 0 })
    ));

    // eslint-disable-next-line no-shadow
    const nextPermissions = _.unionWith(newPermissions, oldFiltered, _.isEqual);

    dispatch(updateRole(id, activeRole.id, {
      permissions: nextPermissions,
    }));
  };

  const availableDetails = useMemo(() => {
    if (!_.isEmpty(activeRole) && allPermissions?.length) {
      // eslint-disable-next-line no-shadow
      return allPermissions.filter((perm) => activeRole.accountRolesPermissions.some(({ id }) => id === perm.id));
    }
    return [];
  }, [activeRole, allPermissions]);

  useEffect(() => {
    dispatch(getRoles(id));
    dispatch(loadEmployeesAll(id));
    dispatch(getAccountGroups(id));
    dispatch(loadLogbookJournal(id));
    dispatch(loadIntegrations(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!allPermissions?.length) {
      dispatch(loadPermissions(id));
    }

    // eslint-disable-next-line
  }, [dispatch, id, allPermissions?.length]);

  useEffect(() => {
    setDefaultRoleAccess(() => {
      const { moduleAccess, organisation } = initialRoleAccess;

      const nextModuleAccess = Object.keys(moduleAccess).reduce((acc, key) => {
        acc[key] = {
          options: {
            ...moduleAccess[key].options,
          },
          enabled: true,
        };
        return acc;
      }, {});
      const nextOrganisation = Object.keys(organisation).reduce((acc, key) => {
        acc[key] = {
          ...organisation[key],
          enabled: true,
        };
        return acc;
      }, {});

      /* permissions */
      if (!permissions.logbook) {
        delete nextModuleAccess.logbook;
      } else {
        if (!permissions.use_approval_flow || !journal.approve_flow) {
          delete nextModuleAccess.logbook.options.requests;
          delete nextModuleAccess.logbook.options.requests_in_place;
        }

        if (!permissions.cost_earning) {
          delete nextModuleAccess.logbook.options.costs;
          delete nextModuleAccess.logbook.options.earnings;
        }

        if (!permissions.profitability) {
          delete nextModuleAccess.logbook.options.profit;
        }
      }
      if (!permissions.reports) {
        delete nextModuleAccess.reports;
      } else {
        if (!permissions.cost_earning) {
          delete nextModuleAccess.reports.options.costs;
        }
        if (!permissions.profitability) {
          delete nextModuleAccess.reports.options.earnings;
          delete nextModuleAccess.reports.options.profit;
        }
      }
      if (!permissions.events) {
        delete nextModuleAccess.events;
      }

      if (!permissions.schedule_shift) {
        // delete nextModuleAccess.events;

        if (!permissions.schedule_simple) {
          delete nextModuleAccess.schedule;
          delete nextOrganisation.schedule;
        } else if (nextModuleAccess.schedule?.options) {
          delete nextModuleAccess.schedule.options.assigned_place;
        }
      }
      if (!permissions.activity_log) {
        delete nextOrganisation.activity_log;
      }

      if (!permissions.time_off) {
        delete nextModuleAccess.time_off;
        delete nextOrganisation.time_off;
      }
      if (!permissions.places) {
        delete nextModuleAccess.places;
      }
      if (!permissions.places_use_documents) {
        delete nextModuleAccess.places?.options?.can_manage_documents;
      }
      if (!permissions.places_profitability) {
        delete nextModuleAccess.places?.options?.can_see_profitability;
      }
      if (!permissions.invoices) {
        delete nextModuleAccess.invoices;
      }

      return {
        organisation: nextOrganisation,
        moduleAccess: nextModuleAccess,
      };
    });

    // eslint-disable-next-line
  }, [permissions, journal, integrations]);

    useEffect(() => {
      if (roles?.length && !_.isEmpty(activeRole)) {
        // eslint-disable-next-line no-shadow
        const role = roles.find(({id}) => id === activeRole.id);
        setActiveRole(role);
      }
    }, [activeRole, roles]);

  const createNewRole = (roleName) => {
    if (roleName.trim()) {
      dispatch(createRole(id, roleName.trim()));
      setNewRoleOpen(false);
    }
  };

  const changeRoleName = (roleName) => {
    if (roleName.trim()) {
      dispatch(updateRole(id, activeRole.id, { name: roleName.trim() }));
      setEditVisible(false);
    }
  };

  const changeDefaultRole = (roleId, checked) => {
    dispatch(updateRole(id, roleId, { default: checked ? 1 : 0 }));
  };

  const removeRole = (roleId) => {
    dispatch(deleteRole(id, roleId));
  };

  return (
    <MaynLayout>
      <Dashboard>
        <TitleBlock
          title={t('Roles')}
        >
          <RolesIcon />
        </TitleBlock>
        <PageLayout>
          {
            isLoading ? <Progress />
              : (
                <RolesBlock
                  roles={roles}
                  activeRole={activeRole}
                  setActiveRole={setActiveRole}
                  createNewRole={() => setNewRoleOpen(true)}
                  remove={removeRole}
                  changeDefaultRole={changeDefaultRole}
                  loading={loading}
                  setEditVisible={setEditVisible}
                  availableDetails={availableDetails}
                  employees={employees}
                  groups={groups}
                  roleEmployeesEdit={roleEmployeesEdit}
                  rolesPermissionsEdit={rolesPermissionsEdit}
                  permissionsIds={permissionsIds}
                  removeRolesPermissions={removeRolesPermissions}
                  defaultRoleAccess={defaultRoleAccess}
                  permissions={permissions}
                  user={user}
                />
              )
          }
          <Snackbar
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            ContentProps={{
              classes: {
                root: typeSnackbar === 'error' ? classes.error : classes.success,
              },
            }}
            severity='error'
            open={isSnackbar}
            message={textSnackbar}
            key='rigth'
          />
          <AddEditItem
            open={editVisible}
            handleClose={() => {
              setEditVisible(false);
            }}
            initialValue={activeRole?.name}
            title={t('Edit role name')}
            buttonTitle={t('Change name')}
            label={t('Role name')}
            placeholder={t('Enter role name')}
            onSubmit={changeRoleName}
          />
          <AddEditItem
            open={newRoleOpen}
            handleClose={() => {
              setNewRoleOpen(false);
            }}
            title={t('Create a new role')}
            onSubmit={createNewRole}
            buttonTitle={t('Create Role')}
            label={t('Role name')}
            placeholder={t('Enter role name')}
          />
        </PageLayout>
      </Dashboard>
    </MaynLayout>
  );
};
